import { FC, useState } from 'react'
import { TotalMentionsCard } from 'src/pages/Campaigns/HeroSection/Cards/TotalMentionsCard'
import { TotalSourcesCard } from 'src/pages/Campaigns/HeroSection/Cards/TotalSourcesCard'
import { TopKeywordCard } from 'src/pages/Campaigns/HeroSection/Cards/TopKeywordCard'
import { TopSourceCard } from 'src/pages/Campaigns/HeroSection/Cards/TopSourceCard'
import { TopDayCard } from 'src/pages/Campaigns/HeroSection/Cards/TopDayCard'
import { SentimentScoreCard } from 'src/pages/Campaigns/HeroSection/Cards/SentimentScoreCard'
import { PlatformBreakdownView } from 'src/pages/Campaigns/HeroSection/PlatformBreakdownView'
import {
  AnalyticsFetchDailyTrendsResponse,
  AnalyticsFetchTopSourcesResponse,
  CampaignsOverviewData,
} from 'src/services/__generated__/api'
import styles from './campaignDetailHeroSection.module.scss'

type ViewMode = 'general' | 'platform'

interface CampaignDetailHeroSectionProps {
  dailyTrends?: AnalyticsFetchDailyTrendsResponse | null
  isLoading?: boolean
  overview?: CampaignsOverviewData
  sourcesList?: string[]
  topSourcesData?: AnalyticsFetchTopSourcesResponse
}

const ratioToPercent = (ratio?: number): number =>
  ratio === undefined || ratio === null ? 0 : Math.round(ratio * 100)

// Sentiment score on a 0–100 scale centered at 50:
// 50 + (positive − negative) / total × 50  ==  50 + (posRatio − negRatio) × 50.
const calcScore = (positiveRatio?: number, negativeRatio?: number): number =>
  Math.round(50 + ((positiveRatio ?? 0) - (negativeRatio ?? 0)) * 50)

export const CampaignDetailHeroSection: FC<CampaignDetailHeroSectionProps> = ({
  dailyTrends,
  isLoading = false,
  overview,
  sourcesList = [],
  topSourcesData = [],
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('general')

  const mentions = overview?.mentions
  const totalMentions = mentions?.total ?? 0

  const positivePercentage = ratioToPercent(mentions?.positiveRatio)
  const neutralPercentage = ratioToPercent(mentions?.neutralRatio)
  const negativePercentage = ratioToPercent(mentions?.negativeRatio)
  const score = calcScore(mentions?.positiveRatio, mentions?.negativeRatio)

  const sourcesUniqueCount =
    overview?.sources?.uniqueCount ?? sourcesList.length
  // These no longer absorb isLoading. They used to read `isLoading || x`, which
  // forced each card's "has data" branch open during the first load and printed
  // 0 / 0% / a "Poor" sentiment score as though they were measured. Each card
  // now takes isLoading and renders skeletons, so these flags describe only
  // whether data genuinely exists — keeping the empty state reachable.
  const hasMentions = totalMentions > 0
  const hasSources = sourcesUniqueCount > 0
  const hasTopKeyword = Boolean(
    overview?.topKeyword?.mostPositive || overview?.topKeyword?.mostNegative
  )
  const hasTopSource = Boolean(
    overview?.sources?.mostPositive || overview?.sources?.mostNegative
  )
  const hasTopDay = Boolean(dailyTrends?.dates?.length)

  return (
    <div className={styles.heroSection}>
      {/* Header with toggle */}
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>Mentions Overview</span>
        <div className={styles.toggleContainer}>
          <button
            className={`${styles.toggleButton} ${viewMode === 'general' ? styles.toggleButtonActive : ''}`}
            onClick={() => setViewMode('general')}
          >
            General Overview
          </button>
          <button
            className={`${styles.toggleButton} ${viewMode === 'platform' ? styles.toggleButtonActive : ''}`}
            onClick={() => setViewMode('platform')}
          >
            Platform Breakdown
          </button>
        </div>
      </div>

      {/* Rows only shown in General Overview */}
      {viewMode === 'general' && (
        <>
          {/* Row 1 — 3 cards */}
          <div className={styles.row}>
            <TotalMentionsCard
              hasData={hasMentions}
              isLoading={isLoading}
              negativeCount={mentions?.negative ?? 0}
              negativePercentage={negativePercentage}
              neutralCount={mentions?.neutral ?? 0}
              neutralPercentage={neutralPercentage}
              positiveCount={mentions?.positive ?? 0}
              positivePercentage={positivePercentage}
              total={totalMentions}
            />

            <TotalSourcesCard
              hasData={hasSources}
              isLoading={isLoading}
              sources={sourcesList}
              uniqueCount={sourcesUniqueCount}
            />

            <SentimentScoreCard
              hasData={hasMentions}
              isLoading={isLoading}
              negativePercentage={negativePercentage}
              neutralPercentage={neutralPercentage}
              positivePercentage={positivePercentage}
              score={score}
            />
          </div>

          {/* Row 2 — 3 cards */}
          <div className={styles.row}>
            <TopKeywordCard
              hasData={hasTopKeyword}
              isLoading={isLoading}
              mostNegative={overview?.topKeyword?.mostNegative}
              mostPositive={overview?.topKeyword?.mostPositive}
            />

            <TopSourceCard
              hasData={hasTopSource}
              isLoading={isLoading}
              mostNegative={overview?.sources?.mostNegative}
              mostPositive={overview?.sources?.mostPositive}
            />

            <TopDayCard
              dailyTrends={dailyTrends}
              hasData={hasTopDay}
              isLoading={isLoading}
            />
          </div>
        </>
      )}

      {/* Platform Breakdown */}
      {viewMode === 'platform' && (
        <PlatformBreakdownView
          isLoading={isLoading}
          overallMentions={overview?.mentions?.total ?? 0}
          sources={topSourcesData}
        />
      )}
    </div>
  )
}
